import { format } from 'date-fns';
import { AlertCircle, Check, CheckCheck } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { WhatsAppMessage } from '../types';

interface WhatsAppMessageBubbleProps {
  message: WhatsAppMessage;
}

function StatusIcon({ status }: { status: string }) {
  const size = 13;
  switch (status) {
    case 'read':
      return <CheckCheck size={size} color={colors.primary.neon} />;
    case 'delivered':
      return <CheckCheck size={size} color={colors.text.tertiary} />;
    case 'sent':
      return <Check size={size} color={colors.text.tertiary} />;
    case 'failed':
      return <AlertCircle size={size} color={colors.status.error.main} />;
    default:
      return <Check size={size} color={colors.text.disabled} />;
  }
}

export function WhatsAppMessageBubble({ message }: WhatsAppMessageBubbleProps) {
  const isOutbound = message.direction === 'outbound';
  const timestamp = message.sentAt || message.createdAt;
  const timeStr = format(new Date(timestamp), 'HH:mm');

  const displayText =
    message.textBody ||
    (message.templateName ? `[Template: ${message.templateName}]` : '[Media message]');

  return (
    <Box style={[styles.row, isOutbound ? styles.rowOutbound : styles.rowInbound]}>
      <Box style={[styles.bubble, isOutbound ? styles.bubbleOutbound : styles.bubbleInbound]}>
        {message.templateName && (
          <Text
            variant="caption"
            color={isOutbound ? colors.primary[800] : colors.primary.main}
            style={styles.templateLabel}
          >
            {message.templateName}
          </Text>
        )}
        <Text
          variant="body"
          color={isOutbound ? colors.text.inverse : colors.text.primary}
          style={styles.bodyText}
        >
          {displayText}
        </Text>
        <HStack spacing="xs" align="center" style={styles.meta}>
          <Text
            variant="caption"
            color={isOutbound ? colors.primary[700] : colors.text.tertiary}
            style={styles.time}
          >
            {timeStr}
          </Text>
          {isOutbound && <StatusIcon status={message.status} />}
        </HStack>
        {message.status === 'failed' && message.errorMessage && (
          <Text variant="caption" color={colors.status.error.light} style={styles.errorText}>
            {message.errorMessage}
          </Text>
        )}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create({
  row: {
    paddingHorizontal: tokens.spacing.md,
    marginVertical: 2,
  },
  rowOutbound: {
    alignItems: 'flex-end',
  },
  rowInbound: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: '80%',
    borderRadius: tokens.radius.lg,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.sm,
  },
  bubbleOutbound: {
    backgroundColor: colors.primary[600],
    borderBottomRightRadius: tokens.radius.sm,
  },
  bubbleInbound: {
    backgroundColor: colors.background.secondary,
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderBottomLeftRadius: tokens.radius.sm,
  },
  templateLabel: {
    fontSize: 10,
    fontWeight: tokens.fontWeight.semibold,
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  bodyText: {
    fontSize: tokens.fontSize.base,
    lineHeight: tokens.fontSize.base * tokens.lineHeight.normal,
  },
  meta: {
    justifyContent: 'flex-end',
    marginTop: 2,
  },
  time: {
    fontSize: 10,
  },
  errorText: {
    fontSize: 10,
    marginTop: 2,
  },
});
